import { subscribeBackIntentChange } from './backIntentBus.js';
import { getEscapeCloseStackSize, invokeTopEscapeClose } from './useEscapeCloseStack.js';
import { getAppBackStackSize, invokeTopAppBack } from './useAppBackButton.js';

/** Marker copied onto history.state so Android/browser Back can be intercepted. */
export const APP_BACK_GUARD = 'rentalyzerAppBack';

let installed = false;
let win = null;
let guardDepth = 0;
let pendingSilentPops = 0;
let syncScheduled = false;
let unsubscribeBus = null;

function isGuardState(state) {
  return Boolean(state && typeof state === 'object' && state[APP_BACK_GUARD] === true);
}

function needGuard() {
  return getEscapeCloseStackSize() > 0 || getAppBackStackSize() > 0;
}

function pushGuard() {
  if (!win || guardDepth > 0) return;
  const prev = win.history.state;
  const prevObj = prev && typeof prev === 'object' && !Array.isArray(prev) ? prev : {};
  win.history.pushState(
    { ...prevObj, [APP_BACK_GUARD]: true },
    '',
    win.location.href,
  );
  guardDepth = 1;
}

function popGuardSilently() {
  if (!win || guardDepth === 0) return;
  guardDepth = 0;
  // React Router may already have replaced this entry (e.g. redirect to /login).
  if (!isGuardState(win.history.state)) return;
  pendingSilentPops += 1;
  win.history.back();
}

function syncGuard() {
  if (!win) return;
  const need = needGuard();
  if (need && guardDepth === 0) pushGuard();
  else if (!need && guardDepth > 0) popGuardSilently();
}

function scheduleSync() {
  if (syncScheduled) return;
  syncScheduled = true;
  queueMicrotask(() => {
    syncScheduled = false;
    syncGuard();
  });
}

function onPopState() {
  if (pendingSilentPops > 0) {
    pendingSilentPops -= 1;
    return;
  }
  // Android/browser Back already consumed the guard entry.
  guardDepth = 0;

  if (invokeTopEscapeClose()) {
    scheduleSync();
    return;
  }
  if (invokeTopAppBack()) {
    scheduleSync();
    return;
  }
}

/**
 * Intercepts the platform Back button (Android PWA / browser) so it runs the
 * same action as Rentalyzer's in-app ← Retour, after dismissing overlays.
 */
export function installAppBackHistory(targetWindow) {
  const next = targetWindow || (typeof window !== 'undefined' ? window : null);
  if (!next?.history || installed) return;
  win = next;
  installed = true;
  win.addEventListener('popstate', onPopState);
  unsubscribeBus = subscribeBackIntentChange(scheduleSync);
  scheduleSync();
}

export function resetAppBackHistoryForTests() {
  if (win) win.removeEventListener('popstate', onPopState);
  unsubscribeBus?.();
  unsubscribeBus = null;
  installed = false;
  win = null;
  guardDepth = 0;
  pendingSilentPops = 0;
  syncScheduled = false;
}

export function getAppBackGuardDepthForTests() {
  return guardDepth;
}
